/**
 * Adapted Grid Component.
 *
 * Renders a grid item using the core adapter registry.
 * Routes to component-specific adapters that handle their own data sourcing:
 * - KPI/Markdown: Support inline data OR inputDataset lookup
 * - Table/Chart: Require inputDataset for data lookup
 *
 * Streaming is handled at the data layer: ShowDashboardTool filters out
 * incomplete items (missing component.type) before rendering. Only complete
 * items reach this component.
 */

import { AdaptedChart } from './AdaptedChart.tsx';
import { AdaptedKpi } from './AdaptedKpi.tsx';
import { AdaptedTable } from './AdaptedTable.tsx';
import type { ChatDashboardItem } from './types.ts';
import { AdaptedMarkdown } from '#plugin-chat/components/dashboard/AdaptedMarkdown.tsx';
import { BlockError } from '#plugin-chat/components/shared/BlockError.tsx';

/**
 * Props for AdaptedGridComponent.
 */
export interface AdaptedGridComponentProps {
  item: ChatDashboardItem;
}

/**
 * Component lookup object for rendering grid items by type.
 * Each component type maps to its corresponding adapted component.
 */
const componentRenderers = {
  table: AdaptedTable,
  chart: AdaptedChart,
  kpi: AdaptedKpi,
  markdown: AdaptedMarkdown,
} as const;

/**
 * Adapted Grid Component.
 *
 * Renders a dashboard grid item by routing to the appropriate component adapter.
 * Each adapter handles its own data sourcing strategy:
 * - KPI: Uses inline component data, or fetches via inputDataset
 * - Markdown: Uses inline component.content
 * - Table/Chart: Fetches data via inputDataset (required)
 *
 * Streaming is handled at the data layer: ShowDashboardTool filters out
 * incomplete items before rendering. Only complete items reach this component.
 */
export function AdaptedGridComponent({ item }: AdaptedGridComponentProps) {
  const componentType = item.component?.type;

  if (!componentType) {
    return <BlockError blockType="Component" message="Component type is missing" />;
  }

  const Renderer = componentRenderers[componentType as keyof typeof componentRenderers];

  if (!Renderer) {
    return <BlockError blockType="Component" message={`Unknown component type: ${componentType}`} />;
  }

  return <Renderer item={item} />;
}
